import React from 'react';
import PageHeader from '../components/PageHeader';
import Callout from '../components/Callout';
import { motion } from 'framer-motion';

const Terminology = () => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader 
        badge="Glossary"
        title="Precision in Terminology" 
        subtitle="Before improving our practices, we must agree on what our terms mean. Be aware: definitions vary wildly by field."
      />

      <Callout type="warning" title="A Note on Inconsistency">
        We cannot paper over the fact that different communities use these terms in opposite ways. 
        For instance, computer science often uses "reproducible" where psychology uses "replicable." 
        Always define your terms when writing papers or grants.
      </Callout>

      <h2>The NASEM Consensus (2019)</h2>
      <p>
        To provide a stable foundation, this guide defaults to the definitions established by the 
        U.S. National Academies of Sciences, Engineering, and Medicine in their 2019 consensus report,
        {' '}<a href="https://doi.org/10.17226/25303" target="_blank" rel="noopener noreferrer">Reproducibility and Replicability in Science</a>.
      </p>

      <div className="space-y-6 my-8">
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <h3 className="text-xl text-brand-900 mt-0">Reproducibility</h3>
          <p className="text-sm font-medium text-slate-500 mb-3">Synonyms: Computational Reproducibility</p>
          <p className="m-0">
            Obtaining consistent results using the <strong>same input data</strong>, computational steps, 
            methods, and conditions of analysis. If someone downloads your dataset and runs your code, 
            they should get your exact figures.
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <h3 className="text-xl text-brand-900 mt-0">Replicability</h3>
          <p className="text-sm font-medium text-slate-500 mb-3">Synonyms: Empirical Replicability</p>
          <p className="m-0">
            Obtaining consistent results across studies aimed at answering the same scientific question, 
            each of which has obtained its <strong>own new data</strong>. If another lab runs your experiment 
            on new mice, they should observe the same biological effect.
          </p>
        </div>
      </div>

      <h2>Goodman, Fanelli & Ioannidis's Taxonomy</h2>
      <p>
        Another highly useful mental model is provided by Goodman et al. (2016), who identified that scientists 
        often talk past one another because they are referring to different <em>types</em> of reproducibility:
      </p>

      <ul>
        <li>
          <strong>Methods Reproducibility:</strong> Providing enough detail about study procedures and data 
          so the same procedures could be exactly repeated. (Aligns with NASEM's "Reproducibility").
        </li>
        <li>
          <strong>Results Reproducibility:</strong> Obtaining the same results from the conduct of an independent 
          study whose procedures are as closely matched to the original as possible. (Aligns with NASEM's "Replicability").
        </li>
        <li>
          <strong>Inferential Reproducibility:</strong> The drawing of qualitatively similar conclusions from 
          either an independent replication of a study or a reanalysis of the original study. (Two scientists can 
          look at the exact same perfectly reproducible data and infer completely different conclusions).
        </li>
      </ul>
    </motion.div>
  );
};

export default Terminology;